import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../../style';
import { prefixObjectValues, enums } from '../../../utils';
import {
  DropDownList as KendoDropDownList,
  DropDownListChangeEvent,
  ListItemProps,
} from '@progress/kendo-react-dropdowns';
import { ItemRenderWrapper } from '../simple-styled-components';
import dayjs from 'dayjs';
import * as dm from '../../../data-models';
import { pushAnalyticsDataLayer } from '../../../utils/analytics/helpers';
import { toHTMLElement } from '../../../utils/analytics/kendo-helpers';

type DefaultOption = string | dm.Leave | dm.Claim;

interface ClaimsDropDownListProps {
  keyId?: string;
  id?: string;
  disabled?: boolean;
  caseList: any;
  defaultOption: DefaultOption;
  viewAll?: boolean;
  onChange: (v: any) => void;
  processedProps?: boolean;
  maxWidth?: string;
  value?: any;
  size?: string;
  routedNTNId?: string;
  autoHeight?: boolean;
}

interface CaseProps {
  caseId: string;
  startDate?: string;
  parentId: string;
}

const DropDownList = styled(KendoDropDownList)<any>`
  max-width: 100%;
  width: 100%;

  @media (min-width: 768px) {
    max-width: ${(p) => p.maxwidth};
  }

  .k-dropdown-wrap,
  .k-picker-wrap {
    background-color: ${colors.white};
    border-color: ${colors.alto};
    height: ${(p) => (p.autoheight ? 'auto' : '56px')};
  }
  .k-input,
  option {
    align-self: center;
    color: ${colors.warmNeutralL3};
    font-family: ${fonts.openSans.regular.fontFamily};
    font-weight: ${fonts.openSans.regular.fontWeight};
  }

  @media (max-width: 639px) {
    .k-dropdown-wrap,
    .k-picker-wrap {
      height: 68px;
    }

    .k-input {
      height: 68px;
    }
  }
`;

const SubtitleStyled = styled.div`
  color: ${colors.dustygray};
  font-size: 14px;
`;

const ItemRenderWrapperMod = styled(ItemRenderWrapper)`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  justify-content: space-around;
  padding: 25px 15px;
`;

const Container = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const domIdsStatic = {
  rootNode: 'claims-drop-down-list',
};

export const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

const getReason = (leaveReason: string) => {
  return leaveReason === enums.PregnancyTextChanges.pregnancyBondingLiteral
    ? enums.PregnancyTextChanges.pregnancyBondingLiteralNew
    : leaveReason;
};

const formatCase = (c: any) => {
  //Extract the reason for the claim/leave/accomodation
  let leaveReason = c.reason ? c.reason : c.claimType;

  if (c.accommodationDetails && c.accommodationDetails.length > 0) {
    leaveReason = c.accommodationDetails[0].category;
  }

  leaveReason = getReason(leaveReason);

  //Get the startDate (leave) or claimIncurredDate (claim)
  const startDate = c.startDate ? c.startDate : c.disability?.claimIncurredDate;

  return { leaveReason, startDate, ...c };
};

//Make sure that each claim/leave has an associated caseid.
const formatCasesArray = (caseList: any) => {
  return caseList.map((c: any) => formatCase(c));
};

//The default item must match the correct object type, whether a string or defaulted case.
const getDefaultObject = (defaultValue: any, onChange: any) => {
  if (typeof defaultValue === 'object') {
    const formattedCase = formatCase(defaultValue);
    onChange({ target: { value: formattedCase } });
    return formattedCase;
  }

  return {
    caseId: defaultValue,
    startDate: '1970-01-01',
    endDate: '2100-01-01',
    reason: '',
  };
};

/**
 * A custom styled dropdown component used for displaying a list of claims/leaves. Displays the
 * NTN number, reason, and startDate/created date for the leave/absence.
 *
 * @param p
 */
const ClaimsDropDownList: (p: ClaimsDropDownListProps) => React.ReactElement = (
  p,
) => {
  const domIds = domIdsUnique(p.keyId);
  const id = p.id ?? domIds.rootNode;
  const { defaultOption, onChange, viewAll, value, routedNTNId } = p;
  const [caseList, setCaseList] = React.useState([]);
  const [defaultObject, setDefaultObject] = React.useState({
    caseId: 'Select',
    startDate: '1970-01-01',
    endDate: '2100-01-01',
  });

  React.useEffect(() => {
    if (p.processedProps) {
      const formattedCases = formatCasesArray(p.caseList || []);

      const defaultItem = getDefaultObject(defaultOption, onChange);

      if (viewAll) {
        formattedCases.push(defaultItem);
      }

      const routedCase = formattedCases.find(
        (inputCase: any) => inputCase.caseId === routedNTNId,
      );

      routedCase
        ? setDefaultObject(routedCase)
        : setDefaultObject(value || defaultItem);

      setCaseList(formattedCases);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.caseList, viewAll, p.processedProps]);

  React.useEffect(() => {
    if (value) {
      const newValue =
        caseList.find((fc: any) => fc?.caseId === value?.caseId) ?? value;

      setDefaultObject(newValue);
    }
  }, [value, caseList]);

  React.useEffect(() => {
    if (value) {
      const newValue =
        caseList.find((fc: any) => fc?.caseId === value?.caseId) ?? value;
      JSON.stringify(value) !== JSON.stringify(newValue) &&
        onChange({ target: { value: newValue } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseList]);

  const handleDropDownChange =
    (propsDataGtmDl: any) => (e: DropDownListChangeEvent) => {
      if ((e.nativeEvent as any).key === 'ArrowUp') {
        e.syntheticEvent.stopPropagation();
        e.syntheticEvent.preventDefault();
      }
      // analytics ////////////////////////////////////////////////
      const hostEl: HTMLElement | null =
        toHTMLElement(e?.target?.element) || toHTMLElement(e) || null;

      pushAnalyticsDataLayer(hostEl, propsDataGtmDl, id);
    };

  return (
    <DropDownList
      id={id}
      data={caseList.map((c: CaseProps) => c)}
      itemRender={(li: any, items: any) =>
        DropDownItemRender(li, items, defaultOption)
      }
      valueRender={(element: any, v: any) =>
        valueRender(element, v, defaultOption)
      }
      defaultValue={defaultObject}
      onChange={handleDropDownChange({
        attribute1: 'upload a document',
        attribute2: 'undefined',
        attribute3: 'upload_interaction',
        attribute4: 'undefined',
        label: 'Claim or NTN',
        kendoComponent: 'yes',
      })}
      onClose={onChange}
      disabled={p.disabled}
      maxwidth={p.maxWidth ?? '535px'}
      autoheight={p.autoHeight}
    />
  );
};

const valueRender = (
  element: React.ReactElement<HTMLSpanElement>,
  value: any,
  defaultOption: DefaultOption,
) => {
  if (!value) {
    return element;
  }

  //Defaults
  let children = <div>{value.caseId}</div>;

  if (value.caseId !== defaultOption) {
    children = (
      <div className="tw-flex tw-w-full tw-items-center tw-flex-wrap sm:tw-flex-nowrap">
        <div
          className="
            tw-min-w-0 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap
            tw-basis-full tw-max-w-full tw-mb-1
            sm:tw-basis-auto sm:tw-flex-1 sm:tw-max-w-1/2 sm:tw-mb-0
          "
        >
          {getReason(value.leaveReason || value.reason)}
        </div>

        {value.startDate && (
          <div className="tw-pl-0 sm:pl-2 tw-pr-[30px] tw-shrink-0">
            ({dayjs(value.startDate).format('MM/DD/YY')})
          </div>
        )}

        <div className="tw-ml-auto tw-shrink-0 tw-pl-2">{value.caseId}</div>
      </div>
    );
  }

  return React.cloneElement(element, { ...element.props }, children);
};

/**
 * DropDownItemRender - Renders a custom dropdown options in the caseID filter
 * @param li
 * @param itemProps
 */
const DropDownItemRender = (
  li: React.ReactElement,
  itemProps: ListItemProps,
  defaultOption: DefaultOption,
) => {
  const newLiProps = {
    ...li.props,
    style: {
      padding: 0,
      margin: 0,
      borderBottom: `1px solid ${colors.geyser}`,
    },
  };

  const data = itemProps.dataItem;

  let itemChildren = (
    <Container>
      <ItemRenderWrapperMod>
        <div>{data.caseId}</div>
      </ItemRenderWrapperMod>
    </Container>
  );

  if (itemProps.dataItem.caseId !== defaultOption) {
    itemChildren = (
      <Container>
        <ItemRenderWrapperMod>
          <div className="d-flex">
            {data.leaveReason && (
              <div className="col-7 col-md-8 pl-0 pr-2">{data.leaveReason}</div>
            )}
            {data.startDate && (
              <div className="col-5 col-md-4 pl-0" style={{ textAlign: 'end' }}>
                {dayjs(data.startDate).format('MM/DD/YY')}
              </div>
            )}
          </div>
          <SubtitleStyled className="dropdown-caseid">
            {data.caseId}
          </SubtitleStyled>
        </ItemRenderWrapperMod>
      </Container>
    );
  }

  return React.cloneElement(li, newLiProps, itemChildren);
};

export default ClaimsDropDownList;
